import React from 'react';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ChatIcon from '@mui/icons-material/Chat';
import { styled } from '@mui/material/styles';
import { Divider } from '@mui/material';

import { Avatar, theme, SingleLineText } from '@bloomays-lib/ui.shared';

export type buttonSize = 'large' | 'medium' | 'small';
export type AvatarUsernameProps = {
  name: string;
  jobTitle?: string;
  image?: string;
  size: buttonSize;
  notSeen?: boolean;
  topProfile?: boolean;
  caption?: string;
  commented?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  activeHover?: boolean;
};

const ProfileCard = ({
  name,
  jobTitle,
  image,
  size,
  activeHover = false,
  onClick,
  notSeen,
  topProfile,
  caption,
  commented,
}: AvatarUsernameProps) => {
  return (
    <ProfileContainer onClick={onClick} activeHover={activeHover}>
      <ProfileWrapper>
        <Profile>
          <AvatarInfoContainer>
            <Avatar image={image} size={size} alt={name} />
            <StyledDiv>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <ProfileName topProfile={topProfile} size={size}>
                  {name}
                </ProfileName>
                {notSeen && <NotSeenIcon />}
              </div>
              {jobTitle && (
                <ProfileJobTitle topProfile={topProfile} size={size}>
                  {jobTitle}
                </ProfileJobTitle>
              )}
            </StyledDiv>
          </AvatarInfoContainer>
          {topProfile && <IconTopProfile />}
        </Profile>
        {(caption || commented) && (
          <>
            <Divider orientation="horizontal" flexItem />
            <CaptionContainer hasCaption={!!caption}>
              {caption && <SingleLineText text={caption} variant="caption" />}
              {commented && <IconChat />}
            </CaptionContainer>
          </>
        )}
      </ProfileWrapper>
    </ProfileContainer>
  ) as React.ReactNode;
};

export default ProfileCard;
export { ProfileCard };

const ProfileContainer = styled('div')<{
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  activeHover?: boolean;
}>(({ theme, onClick, activeHover }) => ({
  backgroundColor: theme.palette.neutral.white,
  padding: '10px',
  display: 'flex',
  gap: '10px',
  justifyContent: 'space-between',
  position: 'relative',
  alignItems: 'center',
  minHeight: '80px',
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': activeHover
    ? {
        cursor: onClick ? 'pointer' : 'auto',
        backgroundColor: theme.palette.neutral[10],
        boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.1)',
      }
    : undefined,
}));

const StyledDiv = styled('div')(() => ({
  textAlign: 'left',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
}));

const IconTopProfile = styled(MilitaryTechIcon)(() => ({
  width: '32px',
  height: '32px',
  color: theme.palette.purple[100],
}));

const IconChat = styled(ChatIcon)(() => ({
  width: '18px',
  height: '18px',
  color: theme.palette.neutral[30],
  marginRight: '6px',
}));

const NotSeenIcon = styled(FiberNewIcon)(() => ({
  width: '20px',
  height: '20px',
  color: theme.palette.red[60],
  top: '0',
}));

const ProfileWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
}));

const Profile = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
}));

const ProfileName = styled('div')<{ topProfile?: boolean; size: buttonSize }>(({ topProfile, size }) => ({
  fontFamily: 'Poppins Medium',
  fontSize: size === 'large' ? '18px' : '13px',
  lineHeight: '19.6px',
  color: topProfile ? theme.palette.purple[100] : theme.palette.neutral[90],

  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '12px',
  },
}));

const ProfileJobTitle = styled('div')<{ topProfile?: boolean; size: buttonSize }>(({ topProfile, size }) => ({
  fontFamily: 'Poppins Light',
  fontSize: size === 'large' ? '12px' : '10px',
  lineHeight: size === 'large' ? '12px' : '11px',
  color: topProfile ? theme.palette.purple[100] : theme.palette.neutral[70],
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  maxWidth: '100%',
  paddingBottom: '1px',

  [theme.breakpoints.down('lg')]: {
    fontSize: '12px',
    lineHeight: '13px',
  },

  [theme.breakpoints.down('md')]: {
    fontSize: '11px',
    lineHeight: '12px',
  },
}));

const AvatarInfoContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flex: 1,
  minWidth: 0,
}));

const CaptionContainer = styled('div')<{ hasCaption: boolean }>(({ hasCaption }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  justifyContent: hasCaption ? 'space-between' : 'flex-end',
}));
